import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {ThunkAction} from '../../../../store/appThunk';
import {getHost, getIntegrationsByType} from '../../../../store/data/dataSelectors';
import {fetchIntegrationTypesAsyncAction} from '../../../../store/data/integrationTypes/integrationTypesActions';
import {
  getFlatPluginIntegrationTypes,
  getPluginIntegrationTypes,
} from '../../../../store/data/integrationTypes/integrationTypesSelectors';
import {toggleHideFrameAction} from '../../../../store/data/preferences/preferencesActions';
import {getHiddenFrameIds} from '../../../../store/data/preferences/preferencesSelectors';
import {getIsUserGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {getSortedSidePanelFrames, SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {SidePanelManager} from './sidePanelManager';

/*
 * Selectors.
 */

const getAvailableIntegrationTypes = createSelector(
  [getFlatPluginIntegrationTypes, getIntegrationsByType],
  (integrationTypes, integrations) =>
    integrationTypes.filter((t) => {
      const integration = integrations[t.id];
      return !integration || !integration.isEnabled;
    }),
);

const getIsFramePinned = createSelector(getHiddenFrameIds, (hiddenFrameIds) => {
  const hiddenFrameIdsSet = new Set(hiddenFrameIds);
  return (frame: SidePanelFrame) => !hiddenFrameIdsSet.has(frame.frameId);
});

/*
 * Actions.
 */

function onIntegrationTypeClickAction(integrationType: IntegrationTypeModel): ThunkAction {
  return (dispatch, getState) => {
    // If our user is an admin, open the settings.
    if (getIsUserGlobalAdmin(getState())) {
      dispatch(navigateToLinkAction(`/settings/integrations/native/edit/${integrationType.id}/details`));
      return;
    }

    // Otherwise, open the public website.
    dispatch(navigateToLinkAction(`https://frontapp.com/integrations/${integrationType.id}?non_admin=true`));
  };
}

function onOpenAppDirectoryClickAction(): ThunkAction {
  return (dispatch, getState) => {
    // If our user is an admin, open the settings.
    if (getIsUserGlobalAdmin(getState())) {
      dispatch(navigateToLinkAction(`/settings/integrations/native`));
      return;
    }

    // Otherwise, open the public website.
    dispatch(navigateToLinkAction(`https://frontapp.com/integrations`));
  };
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  host: getHost,
  integrationTypes: getPluginIntegrationTypes,
  availableIntegrationTypes: getAvailableIntegrationTypes,
  frames: getSortedSidePanelFrames,
  isFramePinned: getIsFramePinned,
}));

const dispatchProps = {
  onTogglePinFrameClick: (frame: SidePanelFrame) => toggleHideFrameAction(frame.frameId),
  onIntegrationTypeClick: onIntegrationTypeClickAction,
  onOpenAppDirectoryClick: onOpenAppDirectoryClickAction,
  fetchIntegrationTypesAsync: fetchIntegrationTypesAsyncAction,
};

export const SidePanelManagerContainer = connect(mapStateToProps, dispatchProps)(SidePanelManager);
